import { Drawer, DrawerProps } from '@mui/material';
import {
    createContext,
    FunctionComponent,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { ConfirmationDialog } from '../confirmationDialog/confirmationDialog';

const DirtyContext = createContext<((dirty: boolean) => void) | null>(null);

type FormDrawerProps = Omit<DrawerProps, 'onClose'> & { onClose: () => void };

/**
 * Drawer for edit forms. Backdrop click or Escape asks before throwing away
 * unsaved changes; the form inside reports them with `useDirtyGuard`.
 * Buttons inside the form (save, cancel) call `onClose` directly.
 */
export const FormDrawer: FunctionComponent<FormDrawerProps> = ({
    onClose,
    children,
    ...drawerProps
}) => {
    const dirtyRef = useRef(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const setDirty = useCallback((dirty: boolean) => {
        dirtyRef.current = dirty;
    }, []);

    return (
        <>
            <Drawer
                {...drawerProps}
                onClose={() =>
                    dirtyRef.current ? setConfirmOpen(true) : onClose()
                }
            >
                <DirtyContext.Provider value={setDirty}>
                    {children}
                </DirtyContext.Provider>
            </Drawer>
            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    setConfirmOpen(false);
                    dirtyRef.current = false;
                    onClose();
                }}
                body='Ungespeicherte Änderungen verwerfen?'
                confirmButtonText='Verwerfen'
            />
        </>
    );
};

/** Tells the surrounding FormDrawer whether the form has unsaved changes. */
export const useDirtyGuard = (dirty: boolean) => {
    const setDirty = useContext(DirtyContext);

    useEffect(() => {
        setDirty?.(dirty);
        return () => setDirty?.(false);
    }, [dirty, setDirty]);
};
